/**
 * ZAGX v2 — Bonus Tracker & Projection Engine
 *
 * Manages commission/bonus plans by fiscal year with quarterly tracking,
 * gross-to-net calculation, allocation recommendations, and YTD analytics.
 *
 * Data model:
 *   bonusPlans[]: { id, label, fiscalYear, startDate, endDate, targetEUR, currency, allocFramework[] }
 *   bonusQuarters[]: { id, planId, quarter, status, grossEUR, fxRate, grossTRY, netTRY, netBrutRatio, earnedDate, paidDate, allocations[], note }
 *
 * Status flow: projected → earned → paid → allocated
 */

import { S, save, stampItem, tombstoneId } from '../core/state.js';
import { esc, uid, now, f, usdToTRY, fxEURTRY } from '../core/utils.js';
import { sizingIndex, sizingOfPortfolio } from '../core/portfolio-policy.js';
import { cyclePhase, evaluateGates, cycleAllocation, planForMonth, planDiscrepancies, PLAN as CYCLE_PLAN } from '../core/cycle-phase.js';
import { reserveCoverageUSD } from '../modules/cash.js';
import { reserveTargetUSD, todayLocal } from '../modules/cashflow.js';
import { calcDrift, holdingCategory } from '../modules/rebalancing.js';
import { planDeployment, plannedBuys } from '../core/bonus-engine.js';
import { riskScore } from '../core/risk-calc.js';
import { toast, clMo } from '../ui/layout.js';
import { uiConfirm } from '../ui/dialog.js';
import { hPnl, hVal, toUSD, normTicker } from '../core/math.js';
import { refreshPrices } from '../core/prices.js';

// ─── Helpers ───
const fT = (v) => v == null ? '—' : '₺' + Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 0 });
// `heldByGate` is the ONE driver on this page denominated in USD: cycleAllocation() splits the
// approved plan's own USD figure, and a blocked leg's share stays in that unit. It was rendered
// with fT, which printed a dollar amount behind a lira sign.
const fU = (v) => v == null ? '—' : '$' + Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 });
const fE = (v) => v == null ? '—' : '€' + Number(v).toLocaleString('de-DE', { maximumFractionDigits: 0 });
const fP = (v) => v == null ? '—' : (v * 100).toFixed(1) + '%';
const _pc = (v) => v > 0 ? 'txt-g' : v < 0 ? 'txt-r' : '';

function _activePlan() {
  const plans = S.bonusPlans || [];
  // Find plan whose date range includes today, or most recent
  const today = new Date().toISOString().slice(0, 10);
  return plans.find(p => p.startDate <= today && p.endDate >= today) || plans[plans.length - 1] || null;
}

function _quartersForPlan(planId) {
  return (S.bonusQuarters || []).filter(q => q.planId === planId).sort((a, b) => {
    const qOrder = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 };
    return (qOrder[a.quarter] || 0) - (qOrder[b.quarter] || 0);
  });
}

function _statusBadge(s) {
  const map = {
    projected: '<span class="badge" style="background:var(--s2);color:var(--bg)">Projected</span>',
    earned: '<span class="badge" style="background:var(--yel);color:#000">Earned</span>',
    paid: '<span class="badge" style="background:var(--grn);color:#000">Paid</span>',
    allocated: '<span class="badge" style="background:var(--a1);color:#fff">Allocated</span>'
  };
  return map[s] || esc(s);
}

// ─── Gross-to-Net Calculator ───
// Turkish progressive income tax brackets (2026 estimates based on cumulative income)
// Calendar quarters: Q1=Jan-Mar, Q2=Apr-Jun, Q3=Jul-Sep, Q4=Oct-Dec
// Tax year resets Jan 1 — Q1 has lowest cumulative, Q4 highest
function estimateNetBrut(quarterIndex, _annualGrossTRY) {
  // Simplified model — user can override with actual net after payment
  if (quarterIndex <= 1) return 0.64;    // Q1-Q2: low cumulative, fresh tax year
  if (quarterIndex === 2) return 0.59;   // Q3: cumulative pushes into higher bracket
  return 0.62;                           // Q4: highest cumulative, marginal rate stabilizes
}

// ─── Plan Selector ───
let _selectedPlanId = null;

function _getSelectedPlan() {
  if (_selectedPlanId) {
    const found = (S.bonusPlans || []).find(p => p.id === _selectedPlanId);
    if (found) return found;
  }
  const active = _activePlan();
  if (active) _selectedPlanId = active.id;
  return active;
}

// ─── RENDER ───
// ═══════════════════════════════════════════════════════════════
// ALLOCATION SCAN — deterministic, state-aware deployment planner.
// Reads live reserve gap, US-sleeve drift, deep-underwater (DCA) holdings,
// and the cached Fear&Greed macro overlay; resolves the SAME net amount into
// three architectures (Conservative / Balanced / Risky). Projected = today's
// picture; when the quarter is paid you re-scan and the split re-solves.
// No AI decision — deterministic core + macro nudge (his "deterministic > LLM").
// ═══════════════════════════════════════════════════════════════
let _bonusProfile = 'balanced';
let _bonusLens = 'auto';

/** Most-underweight retirement categories (from rebalancing drift), worst first. */
function _topUnderweights() {
  try { return (calcDrift().drifts || []).filter(x => x.driftPP < -2).sort((a, b) => a.driftPP - b.driftPP); }
  catch (_e) { return []; }
}
/** Open positions the last AI scan flagged bullish, ranked by confidence (Trade lens source). */
/** Exported for tests: the de-duplication below is the whole subject. */
export function _tradeConviction() {
  const held = new Set([
    ...(S.trades || []).filter(t => t.status !== 'Closed' && t.type !== 'TimeDeposit').map(t => t.ticker),
    ...(S.holdings || []).map(h => h.ticker),
  ]);
  return (S.aiCopilot?.impacts || [])
    .filter(im => im.direction === 'bullish' && (im.confidence || 0) >= 0.5 && (im.tickers || []).some(t => held.has(t)))
    .map(im => ({ ticker: (im.tickers || []).find(t => held.has(t)), conf: im.confidence || 0, why: im.rationale || im.evidence_quote_en || im.evidence_quote || '' }))
    // Two impacts can name the same ticker, and the label read "CVX, CVX · CVX / CVX" while the
    // whole tranche pointed at one position counted twice. Keep the strongest read per name.
    .reduce((acc, x) => {
      if (!x.ticker) return acc;
      const prev = acc.find(y => y.ticker === x.ticker);
      if (!prev) acc.push(x);
      else if (x.conf > prev.conf) Object.assign(prev, x);
      return acc;
    }, [])
    .sort((a, b) => b.conf - a.conf)
    .slice(0, 2);
}
const _BSPAGES = { cash: 'cash', portfolio: 'portfolio', goals: 'goals', trades: 'trades', signals: 'signals' };

// Reserve coverage/target via the canonical SSOT (cash.js reserveCoverageUSD +
// cashflow.js reserveTargetUSD). Any new reserve component added there flows here
// automatically — never re-sum reserve in this module.
// null, not `|| 1`: these two size the reserve tranche the user actually deploys.
// (audit 2026-08-01)
const _reserveNowTRY = () => usdToTRY(reserveCoverageUSD());
const _reserveTargetTRY = () => usdToTRY(reserveTargetUSD());
const _valueTRY = (h) => {
  try { const v = hVal(h); const u = (v && v.vU) || 0; return S.fxRate > 0 ? u * S.fxRate : null; }
  catch (_e) { return null; }
};

/**
 * ticker -> total TRY value, across EVERY holding row that names it PLUS any open trade on it.
 * One name, one number: the average-down leg and the conviction leg used to disagree about
 * what the same position was worth because one summed per-holding and the other took
 * `holdings.find()`. This is also what bounds the legs against each other.
 */
/** Exported for tests: which sleeves count as room is the subject. */
export function _tickerValueTRY() {
  const fx = S.fxRate > 0 ? S.fxRate : null;
  if (!fx) return null;
  const out = Object.create(null);
  // Only the sleeves a plan may propose against count as room. Value parked in an `accumulate`
  // sleeve is not headroom for a buy: the whole point of that sizing is that the app does not
  // act there. Counting it made a weighted leg 46x the position it could really add to.
  const _sz = Object.create(null);
  (S.portfolios || []).forEach(pf => { if (pf && pf.id) _sz[pf.id] = sizingOfPortfolio(pf); });
  const _actionable = pid => _sz[pid] === 'weighted' || _sz[pid] === 'cyclical';
  // Keyed by `normTicker`, the holdings panel's own rule, so a THYAO trade and a THYAO.IS
  // holding are ONE position to the cap as they are to everything else.
  (S.holdings || []).filter(h => h && h.ticker && !h._del && _actionable(h.portfolioId)).forEach(h => {
    const v = _valueTRY(h);
    if (v > 0) { const k = normTicker(h.ticker); out[k] = (out[k] || 0) + v; }
  });
  // Open trades, valued the way `risk-calc.tradeExposure` values them: price x qty x LEVERAGE,
  // through `toUSD`. The hand-rolled version omitted leverage and treated every non-TRY currency
  // as dollars, so a 10x position read 1x and a EUR position read ~11% light -- a hard limit on
  // one page contradicting the Trades page by up to ten times.
  (S.trades || []).filter(t => t && t.ticker && t.status !== 'Closed' && t.type !== 'TimeDeposit').forEach(t => {
    // A SHORT offers no room to buy into. Reducing a short is closing it, not averaging down,
    // and this number answers "how much may I add", not "how much am I exposed to".
    if (String(t.side || '').toLowerCase() === 'short') return;
    const q = (S.prices && (S.prices[t.ticker] || S.prices[normTicker(t.ticker)])) || null;
    const px = (q && q.price) || 0;
    const qty = Number(t.qty) || 0;
    const lev = Number(t.leverage) || 1;
    if (!(px > 0) || !(qty > 0)) return;
    const usd = Math.abs(toUSD(px * qty * lev, t.currency || 'USD'));
    if (usd > 0) { const k = normTicker(t.ticker); out[k] = (out[k] || 0) + usd * fx; }
  });
  return out;
}
const _tv = (map, tk) => (map && tk) ? map[normTicker(tk)] : null;
/** Exported for tests: the de-dup and the sleeve preference are the subject. */
export function _deepLosers() {
  const seen = new Set();
  return (S.holdings || []).filter(h => h && h.ticker && !h._del).map(h => ({ h, p: hPnl(h) }))
    .filter(x => x.p.pnlP != null && x.p.pnlP < -12)
    .sort((a, b) => a.p.pnlP - b.p.pnlP)
    // One row per NAME. `valueTRY` is a per-ticker total, so a name split across two holding
    // rows was counted twice by the cap AND by the union that was supposed to catch that.
    //
    // WHICH row survives decides the sleeve, and the sleeve decides whether the leg exists at
    // all. Keeping the deepest loser meant a one-share row in an accumulate sleeve deleted the
    // plan's only actionable buy leg without a word. Prefer a row the plan may act on.
    .filter(x => { const k = normTicker(x.h.ticker); if (seen.has(k)) return false; seen.add(k); return true; })
    .map(x => {
      const k = normTicker(x.h.ticker);
      const rows = (S.holdings || []).filter(h => h && h.ticker && !h._del && normTicker(h.ticker) === k);
      if (rows.length < 2) return x;
      const sz = h => sizingOfPortfolio((S.portfolios || []).find(pf => pf.id === h.portfolioId));
      if (sz(x.h) === 'weighted') return x;              // already the right row: leave it alone
      // Among the rows the plan MAY act on, take the worst loss -- not whichever happens to be
      // first in the array. Picking by array order let a flat lot beside a deep loser delete
      // the name outright once the P&L was (correctly) recomputed for the row that survived.
      const acts = rows.filter(h => sz(h) === 'weighted').map(h => ({ h, p: hPnl(h) }))
        .filter(y => y.p && y.p.pnlP != null)
        .sort((a, b) => a.p.pnlP - b.p.pnlP);
      return acts.length ? acts[0] : x;
    })
    // A row swapped in may no longer be a deep loser at all. The filter has to run again, or the
    // leg exists because of a loss that is not in it.
    .filter(x => x.p && x.p.pnlP != null && x.p.pnlP < -12);
}
const _usSleeve = () => (S.holdings || []).filter(h => (h.type === 'ETF' || h.type === 'Stock') && (h.currency || 'USD') !== 'TRY').map(h => h.ticker);
const _fearGreed = () => {
  const c = (S.aiCopilot && S.aiCopilot._fgCache) || null;
  const cur = c && (c.current || c); // cache stores { current:{value,label}, history } or the bare object
  return cur && cur.value != null ? { v: cur.value, label: cur.label || cur.classification || '' } : null;
};
function _monthlyIncome() {
  const cf = S.cashflow || {};
  const ks = Object.keys(cf).filter(k => cf[k] && cf[k].income > 0).sort().slice(-3);
  return ks.length ? ks.reduce((s, k) => s + (cf[k].income || 0), 0) / ks.length : 0;
}

/**
 * The net TRY amount to deploy for a quarter (actual if set, else estimate).
 *
 * The EUR leg used to fall back to a hardcoded `53`. That is worse than the `|| 1` this rule
 * was written for: 1 is at least obviously wrong, while 53 is close enough to a real EUR/TRY
 * rate to be believed, and it changes the size of every tranche the bonus planner proposes.
 * A rate that was typed into the source in some earlier month is not a rate. When there is
 * neither a rate on the quarter nor a live one, there is no estimate to make — 0 is returned
 * and the caller shows nothing rather than a number derived from a literal. (audit 2026-08-03)
 */
function _bonusNetTRY(q) {
  if (!q) return 0;
  if (q.netTRY) return q.netTRY;
  let gross = q.grossTRY || 0;
  if (!gross && q.grossEUR > 0) {
    const rate = (q.fxRate > 0 ? q.fxRate : null) || fxEURTRY();
    if (!rate) return 0;                       // no rate, no estimate
    gross = q.grossEUR * rate;
  }
  return gross * (q.netBrutRatio || 0.64);
}

/**
 * Engine context, assembled from live state. Pure read — nothing is written.
 * Model A (calcDrift category bands) sizes; Model B (h.allocationPct, including the
 * qty:0 planned buys) names the instrument inside the category. (audit 2026-08-01)
 */
function _buildBonusCtx(netTRY) {
  const holdings = (S.holdings || []).filter(h => h && !h._del);
  let totalUSD = 0;
  const valued = holdings.map(h => {
    let u = 0;
    try { const v = hVal(h); u = (v && v.vU) || 0; } catch (_e) { u = 0; }
    totalUSD += u;
    return { h, usd: u };
  });
  const heldTargets = valued
    .filter(x => x.h.qty > 0 && (x.h.allocationPct || 0) > 0)
    .map(x => ({
      ticker: x.h.ticker,
      targetPct: x.h.allocationPct || 0,
      actualPct: totalUSD > 0 ? (x.usd / totalUSD) * 100 : 0,
      _h: x.h,
    }));
  const li = S.aiCopilot && S.aiCopilot.lastImpact;
  const impactTs = li && li.ts ? Date.parse(li.ts) : NaN;
  let riskBand = 'healthy';
  try { const r = riskScore(); if (r && r.band) riskBand = r.band; } catch (_e) { /* optional */ }
  const fgCache = (S.aiCopilot && S.aiCopilot._fgCache) || null;
  // `calcDrift` has ALWAYS returned `topLevelAnalysis` beside `drifts`, and this function has
  // always thrown it away. That single omission is the whole defect: the engine could not see
  // that SAP sits 1.8 units under its ceiling or that crypto sits mid-band, so its only crypto
  // signal was `deepLosers` -- drawdown depth -- which put a $326 XRP position first and
  // contradicted the plan the owner approved on 2026-08-11. (2026-09-02)
  let drifts = [], topLevel = null, driftsAvailable = false;
  try {
    const _d = calcDrift();
    if (_d) { drifts = _d.drifts || []; topLevel = _d.topLevelAnalysis || null; driftsAvailable = true; }
  } catch (_e) { drifts = []; topLevel = null; driftsAvailable = false; }

  // `calcDrift` already computes `actionUSD` per category — "positive = need to buy more" —
  // and nothing had ever used it to bound a recommendation. It is the fill tranche's real gap.
  const _fxNow = S.fxRate > 0 ? S.fxRate : null;
  drifts = drifts.map(d => {
    const mid = Number(d.midTarget);
    const denom = isFinite(mid) ? 1 - mid / 100 : 1;
    const need = (isFinite(d.actionUSD) && denom > 0) ? d.actionUSD / denom : d.actionUSD;
    return { ...d, actionTRY: (_fxNow && isFinite(need)) ? need * _fxNow : null };
  });

  const _tvMap = _tickerValueTRY();

  const _ports = S.portfolios || [];
  const _sizing = sizingIndex(_ports);
  const _month = todayLocal().slice(0, 7);
  const _phase = cyclePhase(_month);
  // K3 specifies a WEEKLY CLOSE. What the app holds is the last spot mark, so the basis travels
  // with the gates and the engine says so rather than letting spot pass for a weekly close.
  const _closes = {};
  ['BTC', 'ETH', 'XRP'].forEach(t => { const q = S.prices && S.prices[t]; if (q && q.price > 0) _closes[t] = q.price; });
  const _gates = evaluateGates(_closes, { xrpConfirmed: (S.settings && S.settings.cycleXrpConfirmed) === true });

  // The cycle claim is ABSOLUTE, taken from the approved plan, not a relative weight over the
  // pool. That is deliberate: every non-reserve tranche in this engine is a normalised weight,
  // which is why a pool worth half the portfolio overshot every gap it claimed to close. A leg
  // whose right size is already written down does not get re-derived from a weight.
  const _fx = S.fxRate > 0 ? S.fxRate : null;
  // Hardcoding 'phase1' meant phase 2 could never fire and phase 1's amount would have been
  // claimed past its own 30 Nov deadline. The month picks the phase, and a month no phase names
  // gets no claim at all.
  const _planMonth = planForMonth(_month);
  const _cycleAlloc = _planMonth ? cycleAllocation(_planMonth.usd, _phase, _gates, _planMonth.key) : null;
  const _cycleClaimTRY = (_cycleAlloc && !_cycleAlloc.stopped && _fx) ? Math.round(_cycleAlloc.amount * _fx) : 0;

  return {
    pool: netTRY,
    reserveNowTRY: _reserveNowTRY(),
    reserveTargetTRY: _reserveTargetTRY(),
    drifts,
    plannedBuys: plannedBuys(holdings),
    heldTargets,
    categoryOf: h => { try { return holdingCategory(h && h._h ? h._h : h); } catch (_e) { return null; } },
    // `valueTRY` is what makes a CAP possible: adding more than a position is currently worth
    // stops being "averaging down" and becomes a new, larger bet. (2026-09-02)
    deepLosers: _deepLosers().map(x => ({
      ticker: x.h.ticker, type: x.h.type, pnlP: x.p.pnlP, portfolioId: x.h.portfolioId,
      valueTRY: _tv(_tvMap, x.h.ticker),
    })),
    conviction: _tradeConviction().map(x => ({
      ...x, valueTRY: _tv(_tvMap, x.ticker),
      // The engine filters `deepLosers` by sleeve and never filtered these. Carry the sleeve so
      // it can: a name held only where the app does not act is not a place to add.
      portfolioId: (() => {
        const rows = (S.holdings || []).filter(h => h && h.ticker && !h._del && normTicker(h.ticker) === normTicker(x.ticker));
        const act = rows.find(h => { const z = sizingOfPortfolio((S.portfolios || []).find(pf => pf.id === h.portfolioId)); return z === 'weighted' || z === 'cyclical'; });
        return (act || rows[0] || {}).portfolioId || null;
      })(),
    })),
    // NaN age must read as stale, never as fresh.
    impactAgeMs: isFinite(impactTs) ? Date.now() - impactTs : null,
    fg: _fearGreed(),
    fgHistory: (fgCache && fgCache.history) || [],
    riskBand,
    // ── the three inputs the engine was never given ──
    topLevel,
    driftsAvailable,
    capsAvailable: _fxNow != null,
    tickerValueTRY: _tvMap,
    // Injected, like `categoryOf`: the engine imports nothing, and it must key the value map the
    // same way the map was built or a THYAO.IS instrument finds no room under THYAO.
    normKey: (t) => normTicker(t),
    sizing: _sizing,
    // `fxRate` deliberately does NOT travel here. The lira conversion happens above, where the
    // state is; passing a rate the engine never reads is the same shape as passing `topLevel`
    // and dropping it, which is the defect this whole change exists to close.
    cycle: {
      phase: _phase, gates: _gates, priceBasis: 'spot', month: _month,
      alloc: _cycleAlloc, claimTRY: _cycleClaimTRY,
      planSplit: _planMonth ? _planMonth.split : null,
      // Every coin the plan names in ANY phase. Built from the month, "covered by neither leg"
      // fired on BTC itself in every month with no leg. (round 4)
      allPlanTickers: [...new Set(['phase1', 'phase2'].flatMap(k => Object.keys(CYCLE_PLAN[k].split)))],
      planMonth: _planMonth ? _planMonth.key : null, planUSD: _planMonth ? _planMonth.usd : null,
      discrepancies: planDiscrepancies(),
    },
  };
}

/** Presentation only. The engine decides amounts; this decides the glyph. */
const _TRANCHE_UI = {
  reserve: { ic: '\u{1F6E1}', ac: '#34e2a0', act: 'cash' },
  fill: { ic: '◼', ac: '#4DC9F6', act: 'portfolio' },
  dca: { ic: '◆', ac: 'var(--yel)', act: 'trades' },
  conviction: { ic: '★', ac: 'var(--vio)', act: 'signals' },
  fi: { ic: '◎', ac: 'var(--vio)', act: 'goals' },
  powder: { ic: '◇', ac: 'var(--yel)', act: 'cash' },
  cycle: { ic: '◈', ac: 'var(--cyan)', act: 'trades' },
};

function _trancheDest(t) {
  const names = (t.instruments || []).map(i => i.ticker).filter(Boolean).slice(0, 3);
  return names.length ? `${t.label} · ${names.join(' / ')}` : t.label;
}

/**
 * Copy built from the tranche's OWN drivers. Nothing here may assert a fact the engine
 * did not produce — the previous version stated "your US sleeve is below target weight"
 * as bolded fact on a path that never called calcDrift().
 */
function _trancheWhy(t, ctx, profile) {
  const d = Object.create(null);
  (t.drivers || []).forEach(x => { d[x.key] = x.value; });
  const fgv = d.fearGreed;
  const fgTxt = fgv == null ? 'Fear&Greed is unavailable' : `Fear&Greed <b>${fgv}</b>`;
  const planned = (t.instruments || []).filter(i => i.planned).map(i => i.ticker);
  switch (t.id) {
    case 'reserve':
      return `Reserve is ${fT(ctx.reserveNowTRY)} of ${fT(ctx.reserveTargetTRY)} — a gap of <b>${fT(d.reserveGap)}</b>. ${profile === 'risky' ? 'Risky funds a half floor only and deploys the rest.' : '<span class="bsc-g">This closes the gap in full.</span>'} An unfunded floor is what makes you a forced seller.`;
    case 'fill':
      return `Worst band drift is <b>${d.driftPP}pp</b> below target. ${planned.length ? `<span class="bsc-g">${esc(planned.join(', '))}</span> ${planned.length > 1 ? 'have' : 'has'} a target weight and nothing bought yet — the largest gap in the category. ` : ''}${fgTxt}${fgv != null && fgv < 50 ? ' adds to the case.' : '.'}`;
    case 'dca':
      return `Worst drawdown <b>${d.worstDrawdownPct}%</b> against cost. ${fgTxt}${d.fearTrend7d > 0 ? `, and fear has <b>deepened</b> over 7 days (${d.fearTrend7d})` : ''}. Sized by appetite (${esc(String(d.appetite))}) and damped ×${d.riskDamp} by your composite risk band.`;
    case 'conviction':
      return `Average confidence <b>${d.avgConfidence}</b> across the names your last AI scan flagged bullish on positions you hold. Highest idiosyncratic risk — sized after the reserve and scaled by appetite.`;
    case 'cycle': {
      const ph = String(d.cyclePhase || '');
      const tail = t.drivers && t.drivers.some(x => x.key === 'planTail');
      return `Cycle is in <b>${esc(ph)}</b>, and the amount is the approved plan's own figure`
        + ` (<b>$${esc(String(d.planUSD))}</b>) rather than a share of this bonus.`
        + (tail ? ' This month is on the declared tail, past the halving window on purpose.' : '')
        + (d.xrpGate ? ' <span class="bsc-g">XRP is unconfirmed</span>, so its leg is zero and redistributed 60/40 BTC/ETH.' : '')
        + (d.heldByGate ? ` A price-blocked leg holds ${fU(d.heldByGate)} rather than moving it to another coin.` : '')
        + (d.sleeveBand ? ` The sleeve sits at ${esc(String(d.sleeveBand))} units.` : '')
        + (d.priceBasis === 'spot' ? ' <b>Gates were checked on the last spot mark; the plan specifies a weekly close.</b>' : '');
    }
    case 'fi':
      return `Routed straight at your FI number. Deliberately the steady leg: it does not chase the macro dial, so a windfall compounds against the goal regardless of the cycle.`;
    case 'powder':
      {
        const held = [];
        if (d.belowNoiseFloor !== undefined) held.push(`the <b>${esc(String(d.belowNoiseFloor))}</b> leg(s) came out too small to be decisions at this size of pool`);
        if (d.overCap !== undefined) held.push(`<b>${fT(d.overCap)}</b> was proposed beyond the gap it closes`);
        if (held.length && d.fearGreed === undefined) {
          return `This is not a recommendation, it is what is left: ${held.join(', and ')}. Held rather than folded into whichever tranche happened to be largest.`;
        }
        if (held.length) {
          return `${fgTxt} — the less discount on offer, the more this leg holds. It also carries what no other leg could take: ${held.join(', and ')}.`;
        }
      }
      return `${fgTxt} — the less discount on offer, the more this leg holds. Also grows when appetite is low or the portfolio already carries risk (damp ${d.riskDamp}).`;
    default:
      return t.label;
  }
}

/**
 * Adapter over core/bonus-engine.js. The literal 0.63/0.27/0.10 style splits are gone;
 * every amount below is a function of drift depth, drawdown depth, Fear & Greed level and
 * 7-day direction, AI conviction (freshness-gated), and the composite risk band.
 */
function _bonusArchitecture(netTRY, profile, lens = _bonusLens) {
  const fg = _fearGreed();
  if (!netTRY) return { tranches: [], fg, gap: 0, warnings: [] };
  const ctx = _buildBonusCtx(netTRY);
  const plan = planDeployment(ctx, { profile, lens });
  const gap = (ctx.reserveNowTRY != null && ctx.reserveTargetTRY != null)
    ? Math.max(0, ctx.reserveTargetTRY - ctx.reserveNowTRY) : 0;
  const tranches = plan.tranches.map(t => {
    const ui = _TRANCHE_UI[t.id] || { ic: '◇', ac: 'var(--yel)', act: 'cash' };
    return {
      id: t.id, ic: ui.ic, ac: ui.ac, act: ui.act,
      dest: _trancheDest(t), amt: t.amount, pct: t.pct,
      why: _trancheWhy(t, ctx, profile), drivers: t.drivers || [],
    };
  });
  try { window.__spektrxBonusPlan = { ts: Date.now(), profile, lens, inputs: plan.inputs, warnings: plan.warnings, tranches }; } catch (_e) { /* non-browser */ }
  return { tranches, fg, gap, warnings: plan.warnings || [], inputs: plan.inputs };
}

/** Render the scan section under the Next-Payout hero for the pending quarter. */
function _bonusScanSection(q) {
  if (!q) return '';
  const netTRY = _bonusNetTRY(q);
  if (!netTRY) return '';
  const arch = _bonusArchitecture(netTRY, _bonusProfile);
  const fg = arch.fg;
  const lifecycle = q.status === 'paid' ? 'paid' : q.status === 'earned' ? 'earned' : 'projected';
  const lcNote = lifecycle === 'projected'
    ? "provisional — today's picture; re-scan when the money lands"
    : lifecycle === 'earned' ? 'money confirmed — deploy plan ready' : 'money in hand — execute the plan';

  // Match on the tranche id, never the label. The old label-string equality embedded live
  // percentages ("Crypto DCA · XRP -45%, ETH -40%"), so a locked tranche silently
  // un-marked itself the moment a price moved. (audit 2026-08-01)
  const done = (t) => (q.allocations || []).some(a => a._scan && (a.trancheId ? a.trancheId === t.id : (a.kind === t.act && a.label === _bscPlain(t.dest))));
  const allDone = arch.tranches.length > 0 && arch.tranches.every(done);
  const bar = arch.tranches.map(t => `<i style="flex:${t.pct};background:${t.ac};opacity:${done(t) ? '.45' : '1'}"></i>`).join('');
  const lockRow = allDone
    ? `<div class="bsc-lockrow"><span class="bsc-done">✓ ${esc(q.quarter)} plan locked · ${fT(netTRY)} allocated</span></div>`
    : `<div class="bsc-lockrow"><button class="bsc-lockall" data-action="bonusScanLockAll" data-args='["${q.id}"]'>Lock this ${_bonusProfile} plan → allocate all ${fT(netTRY)}</button></div>`;
  const cards = arch.tranches.map((t, i) => {
    const d = done(t);
    return `<div class="bsc-card${d ? ' done' : ''}" style="--bac:${t.ac}">
      <div class="bsc-top"><div class="bsc-ic">${t.ic}</div><div class="bsc-title">${t.dest}</div>
        <div class="bsc-rank"><b>${fT(t.amt)}</b>#${i + 1} · ${t.pct}%</div></div>
      <div class="bsc-why">${t.why}</div>
      ${(t.drivers || []).length ? `<div class="bsc-drv">${t.drivers.map(dr => `<span><i>${esc(String(dr.key))}</i> ${esc(String(dr.value))}</span>`).join('')}</div>` : ''}
      <div class="bsc-btns">${d
        ? `<span class="bsc-done">✓ Allocated</span><button class="bsc-open" data-action="bonusScanAct" data-args='["${t.act}"]'>${_bscGoLabel(t.act)} →</button>`
        : `<button class="bsc-go" data-action="bonusScanAllocate" data-args='["${q.id}",${i}]'>Allocate ${fT(t.amt)} →</button>`}
        <span class="bsc-meta">scan · ${_bonusProfile}</span></div>
    </div>`;
  }).join('');

  const prof = (id, label, sub) => `<button class="${_bonusProfile === id ? 'on' : ''}" data-action="bonusScanProfile" data-args='["${id}"]'>${label}<span class="s">${sub}</span></button>`;
  const lensBtn = (id, label, sub) => `<button class="${_bonusLens === id ? 'on' : ''}" data-action="bonusScanLens" data-args='["${id}"]'>${label}<span class="s">${sub}</span></button>`;
  const lensNote = {
    fi: '<b>FI focus:</b> tilted at your FI number — accelerator leads, dry powder trimmed. Windfall compounds toward freedom.',
    portfolio: '<b>Portfolio focus:</b> leads with your biggest allocation drift — a lump sum closes the underweight faster than monthly buys.',
    trade: '<b>Trade focus:</b> ranks your open positions by conviction from the last AI scan and adds to the strongest. Higher variance — sized after reserve.',
  }[_bonusLens];
  const mi = _monthlyIncome();
  const months = mi > 0 ? (netTRY / mi).toFixed(1) : null;
  const posLine = arch.gap > 0
    ? (netTRY >= arch.gap ? 'It <span class="bsc-g">closes your entire reserve gap</span> and still deploys.' : 'It materially rebuilds your reserve, then deploys.')
    : 'Your reserve is funded — this is pure deployment capital.';
  const fgColor = fg ? (fg.v <= 30 ? 'var(--red)' : fg.v >= 60 ? 'var(--grn)' : 'var(--yel)') : 'var(--t3)';

  // The engine has ALWAYS produced `warnings` and this page has never shown one. A dropped
  // conviction leg, an undeclared sleeve, a cycle in the wrong phase, a missing FX rate — every
  // one of them silently changed the numbers on screen. (NO SILENT DEGRADATION, 2026-09-02)
  const warnBlock = (arch.warnings || []).length
    ? `<div class="bsc-warn"><div class="bsc-warnhd">⚠ ${arch.warnings.length} thing${arch.warnings.length > 1 ? 's' : ''} this plan could not do silently</div>`
      + `<ul>${arch.warnings.map(w => `<li>${esc(String(w))}</li>`).join('')}</ul></div>`
    : '';

  return `<div class="mny-lbl">ALLOCATION SCAN · ${esc(q.quarter)} · ${lifecycle.toUpperCase()}</div>
    <div class="bsc-sum">
      <div class="bsc-sumhd">
        <div><div class="bsc-sumn">${fT(netTRY)}</div><div class="bsc-sumsub">net to deploy · ${lcNote}</div></div>
        <button class="bsc-scan" data-action="bonusRescan">⟳ Scan now</button>
      </div>
      <div class="bsc-sumctx">${months ? `≈ <b>${months} months of income</b> in one inflow — ` : ''}${posLine} Pick the architecture that fits your appetite.</div>
      <div class="bsc-sumrow"><span>Fear&amp;Greed <b style="color:${fgColor}">${fg ? `${fg.v}${fg.label ? ' · ' + esc(fg.label) : ''}` : 'refresh via AI page'}</b></span><span>reserve gap <b>${fT(arch.gap)}</b></span></div>
    </div>
    ${warnBlock}
    <div class="bsc-axl">Appetite — how aggressive</div>
    <div class="bsc-prof">${prof('conservative', 'Conservative', 'preserve · hedge')}${prof('balanced', 'Balanced', 'floor + deploy')}${prof('risky', 'Risky', 'max the discount')}</div>
    <div class="bsc-axl">Focus — what to optimise</div>
    <div class="bsc-focus">${lensBtn('auto', 'Auto', 'blended')}${lensBtn('portfolio', 'Portfolio', 'fill underweights')}${lensBtn('trade', 'Trade', 'AI conviction adds')}${lensBtn('fi', 'FI', 'accelerate freedom')}</div>
    ${lensNote ? `<div class="bsc-lensnote">${lensNote}</div>` : ''}
    <div class="bsc-bar">${bar}</div>
    ${lockRow}
    ${cards}`;
}
const _bscPlain = (s) => String(s).replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').trim();
const _bscGoLabel = (act) => ({ cash: 'Open Money', portfolio: 'Plan buys', goals: 'Route to goal', trades: 'Plan DCA', signals: 'Review signals' }[act] || 'Open');

/** Re-run the scan on a FRESH picture: refresh prices (→ reserve value, drift, P&L),
 *  showing a Scanning… state like the AI page, then re-solve all three profiles. */
export async function bonusRescan() {
  const btn = document.querySelector('#page-bonus .bsc-scan');
  if (btn) { btn.disabled = true; btn.style.opacity = '.6'; btn.textContent = 'Scanning…'; }
  try { await refreshPrices(); } catch (_e) { /* fall back to cached prices */ }
  toast('Scanned your current picture', 'a-b');
  renderBonus();
}
/** Profile switch (re-renders the bonus page). */
export function bonusScanProfile(p) {
  if (['conservative', 'balanced', 'risky'].includes(p)) { _bonusProfile = p; renderBonus(); }
}
/** Focus-lens switch (orthogonal to profile): auto | portfolio | trade | fi. */
export function bonusScanLens(l) {
  if (['auto', 'portfolio', 'trade', 'fi'].includes(l)) { _bonusLens = l; renderBonus(); }
}
/** Route to a tranche's execution page (the manual/broker leg). */
export function bonusScanAct(kind) {
  const nav = window._navActivateTab;
  const page = _BSPAGES[kind] || 'cash';
  toast(`Opening ${page} to deploy this tranche`, 'a-b');
  if (typeof nav === 'function') nav(page);
}

/** Record ONE tranche onto the quarter's allocation plan (bonus execution). */
export async function bonusScanAllocate(qId, idx) {
  const q = (S.bonusQuarters || []).find(x => x.id === qId);
  if (!q) return;
  const t = _bonusArchitecture(_bonusNetTRY(q), _bonusProfile).tranches[idx];
  if (!t) return;
  const label = _bscPlain(t.dest);
  const ok = await uiConfirm(`Allocate ${fT(t.amt)} to "${label}"?\n\nRecords it on ${q.quarter}'s allocation plan (${_bonusProfile}). Nothing moves at a broker — execute the manual leg from the linked page.`, { title: 'Allocate tranche', okText: 'Allocate' });
  if (!ok) return;
  if (!Array.isArray(q.allocations)) q.allocations = [];
  q.allocations = q.allocations.filter(a => !(a._scan && (a.trancheId ? a.trancheId === t.id : (a.kind === t.act && a.label === label))));
  q.allocations.push({ trancheId: t.id, label, amount: t.amt, pct: t.pct, color: t.ac, kind: t.act, profile: _bonusProfile, _scan: true, executed: false, ts: Date.now() });
  stampItem(S); save();
  toast(`✓ Allocated ${fT(t.amt)} — ${label}`, 'a-g');
  renderBonus();
}

/** Lock the whole current-profile architecture as the quarter's allocation. */
export async function bonusScanLockAll(qId) {
  const q = (S.bonusQuarters || []).find(x => x.id === qId);
  if (!q) return;
  const net = _bonusNetTRY(q);
  const arch = _bonusArchitecture(net, _bonusProfile);
  if (!arch.tranches.length) return;
  const ok = await uiConfirm(`Lock the ${_bonusProfile} plan for ${q.quarter}?\n\nRecords all ${arch.tranches.length} tranches (${fT(net)}) as this quarter's allocation. You can re-scan and re-lock anytime before you deploy.`, { title: 'Lock allocation plan', okText: 'Lock plan' });
  if (!ok) return;
  q.allocations = arch.tranches.map(t => ({ trancheId: t.id, label: _bscPlain(t.dest), amount: t.amt, pct: t.pct, color: t.ac, kind: t.act, profile: _bonusProfile, _scan: true, executed: false, ts: Date.now() }));
  if (q.status === 'paid') q.status = 'allocated';
  stampItem(S); save();
  toast(`✓ ${q.quarter} plan locked (${_bonusProfile})`, 'a-g');
  renderBonus();
}

export function renderBonus() {
  const el = document.getElementById('bonusContent');
  if (!el) return;

  const plans = S.bonusPlans || [];
  if (!plans.length) {
    el.innerHTML = '<div class="neo-empty ctr">No bonus plans configured. Add your first plan to start tracking.</div>' +
      '<div style="text-align:center;margin-top:16px"><button class="btn btn-p" data-action="openBonusPlan">+ Add Bonus Plan</button></div>';
    return;
  }

  const plan = _getSelectedPlan();
  if (!plan) return;
  const quarters = _quartersForPlan(plan.id);

  // ─── YTD Calculations ───
  const earnedEUR = quarters.reduce((s, q) => s + (q.grossEUR || 0), 0);
  const paidQs = quarters.filter(q => q.status === 'paid' || q.status === 'allocated');
  const totalNetTRY = paidQs.reduce((s, q) => s + (q.netTRY || 0), 0);
  const totalGrossTRY = paidQs.reduce((s, q) => s + (q.grossTRY || 0), 0);
  const avgNetBrut = totalGrossTRY > 0 ? totalNetTRY / totalGrossTRY : 0;
  const remainEUR = Math.max(0, (plan.targetEUR || 0) - earnedEUR);
  const pctAchieved = plan.targetEUR > 0 ? earnedEUR / plan.targetEUR : 0;

  // Next expected payment
  const nextQ = quarters.find(q => q.status === 'earned' || q.status === 'projected');
  // The scan is about money that still needs a home. A quarter that is PAID but not yet
  // ALLOCATED is exactly that, and it is the one case the caller never passed -- which left
  // `_bonusScanSection`'s own `lifecycle === 'paid'` branch ("money in hand -- execute the
  // plan") unreachable from the app, reachable only from tests. The hero asks a DIFFERENT
  // question -- what is still owed -- so it keeps reading `nextQ` and keeps saying "None
  // pending". Two questions, two variables. (2026-08-28)
  const scanQ = nextQ || [...quarters].reverse().find(q => q.status === 'paid');
  // The old `|| 53` printed a hardcoded rate as a live-looking "EUR/TRY 53.00" chip next
  // to a pulsing live dot, and sized the NEXT PAYOUT hero from it. (audit 2026-08-01)
  const fxRate = S.fxRateEUR > 0 ? S.fxRateEUR : null;

  // ─── Plan Selector Tabs (mny header) ───
  const planTabs = plans.map(p =>
    `<button class="btn ${p.id === plan.id ? 'btn-p' : 'btn-ghost'}" data-action="switchBonusPlan" data-args='["${esc(p.id)}"]' style="font-size:12px;padding:4px 14px">${esc(p.label)}</button>`
  ).join('');

  // ─── Hero: FY progress ring + next payout (mockup v0.2.1) — same numbers, new shell ───
  const progressPct = Math.min(100, pctAchieved * 100);
  const fw = plan.allocFramework || [];
  const nextGrossTRY = nextQ ? (nextQ.grossTRY || (nextQ.grossEUR || 0) * (nextQ.fxRate || fxRate)) : 0;
  const heroHTML = `
    <div class="mny-hd">
      <span class="mny-live"></span><h1 class="mny-h1">Bonus</h1>
      <span class="mny-meta">earned <b>${fE(earnedEUR)}</b> · ${(pctAchieved * 100).toFixed(1)}% of ${esc(plan.fiscalYear)} target</span>
      <button class="mny-add" data-action="openBonusPlan">+ Plan</button>
    </div>
    <div style="display:flex;gap:8px;margin-top:12px;align-items:center;flex-wrap:wrap">${planTabs}</div>
    <div class="mny-lbl">${nextQ ? 'NEXT PAYOUT' : 'ANNUAL TARGET'}</div>
    <div class="mny-hero">
      <div class="mny-hleft">
        ${nextQ ? `
        <div class="mny-hlbl">${esc(plan.fiscalYear)} ${esc(nextQ.quarter)} · ${esc(String(nextQ.status).toUpperCase())}</div>
        <div class="mny-hnum" style="color:var(--pur,#a78bfa);text-shadow:0 0 24px rgba(167,139,250,.3)">${nextQ.grossEUR ? fE(nextQ.grossEUR) : '—'}</div>
        <div class="mny-chips">
          ${nextGrossTRY ? `<span class="mny-chip">≈ gross <b>${fT(nextGrossTRY)}</b></span>` : ''}
          <span class="mny-chip">avg net/gross <b>${avgNetBrut > 0 ? fP(avgNetBrut) : '—'}</b></span>
          <span class="mny-chip">EUR/TRY <b>${f(fxRate, 2)}</b></span>
        </div>
        <div class="mny-hctx">${fw.length
          ? `Pre-allocation is locked before the money lands — <b>${fw.map(b => `${b.pct}% ${esc(b.label)}`).join(' · ')}</b> — so it never leaks into lifestyle.`
          : 'No allocation framework set — define one in the plan so payouts split themselves.'}</div>
        ${fw.length ? `<div class="mny-allocbar">${fw.map(b => `<i style="flex:${b.pct};background:${esc(b.color)}"></i>`).join('')}</div>` : ''}`
        : `
        <div class="mny-hlbl">ANNUAL TARGET · ${esc(plan.fiscalYear)} (${esc(plan.startDate?.slice(0, 4))}–${esc(plan.endDate?.slice(0, 4))})</div>
        <div class="mny-hnum">${fE(plan.targetEUR)}</div>
        <div class="mny-chips">
          <span class="mny-chip">earned <b style="color:var(--grn)">${fE(earnedEUR)}</b></span>
          <span class="mny-chip">remaining <b style="color:var(--yel)">${fE(remainEUR)}</b></span>
          <span class="mny-chip">EUR/TRY <b>${f(fxRate, 2)}</b></span>
        </div>
        <div class="mny-hctx">All quarters settled — <b>${(pctAchieved * 100).toFixed(1)}%</b> of the annual target realized. None pending.</div>`}
      </div>
      <div class="mny-ring">
        <svg width="130" height="130" viewBox="0 0 120 120">
          <circle class="bgc" cx="60" cy="60" r="54"/>
          <circle class="fgc" cx="60" cy="60" r="54" style="stroke:url(#mnyGradR);stroke-dashoffset:${(339 - 339 * progressPct / 100).toFixed(0)}"/>
        </svg>
        <div class="rlbl"><b>${progressPct.toFixed(0)}%</b><span>OF TARGET</span></div>
      </div>
    </div>
    <div class="mny-kpis">
      <div class="mny-kpi"><div class="l">NET RECEIVED · TRY</div><div class="v pos">${fT(totalNetTRY)}</div><div class="s">${paidQs.length} payout${paidQs.length === 1 ? '' : 's'} settled</div></div>
      <div class="mny-kpi"><div class="l">AVG NET/GROSS</div><div class="v gold">${avgNetBrut > 0 ? fP(avgNetBrut) : '—'}</div><div class="s">after tax &amp; deductions</div></div>
      <div class="mny-kpi"><div class="l">NEXT PAYMENT</div><div class="v cy" style="font-size:15px">${nextQ ? esc(nextQ.quarter) + ' · ' + esc(nextQ.status) : 'None pending'}</div><div class="s">${nextQ && nextQ.grossEUR ? fE(nextQ.grossEUR) + ' gross' : '—'}</div></div>
      <div class="mny-kpi"><div class="l">TARGET PROGRESS</div><div class="v">${(pctAchieved * 100).toFixed(1)}%</div><div class="s">${fE(remainEUR)} to go</div><div class="bar"><i style="width:${progressPct}%"></i></div></div>
    </div>`;

  // ─── Quarter Cards ───
  const allQLabels = ['Q1', 'Q2', 'Q3', 'Q4'];
  const qCards = allQLabels.map((ql, idx) => {
    const q = quarters.find(x => x.quarter === ql);
    if (!q) {
      // Projected quarter — calculate estimate
      const _estNetBrut = estimateNetBrut(idx, 0);
      const _projGrossEUR = remainEUR / Math.max(1, 4 - quarters.length);
      return `<div class="card" style="padding:16px;opacity:.6">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
          <span style="font-weight:600;font-size:15px">${ql}</span>
          <span class="badge" style="background:var(--c3);color:var(--t3)">Not Yet</span>
        </div>
        <div style="color:var(--t3);font-size:12px">Quarter not started</div>
        <button class="btn btn-ghost" style="margin-top:8px;font-size:11px;width:100%" data-action="openBonusQuarter" data-args='[null, "${esc(plan.id)}", "${ql}"]'>+ Add Quarter</button>
      </div>`;
    }

    const hasNet = q.netTRY != null && q.netTRY > 0;
    const estGrossTRY = q.grossTRY || (q.grossEUR * (q.fxRate || fxRate));
    const estNetTRY = q.netTRY || (estGrossTRY * estimateNetBrut(idx, 0));
    const estNetBrut = q.netBrutRatio || (q.grossTRY > 0 && q.netTRY > 0 ? q.netTRY / q.grossTRY : estimateNetBrut(idx, 0));

    // Allocation summary
    const allocs = q.allocations || [];
    const allocHTML = allocs.length > 0
      ? allocs.map(a => `<div style="display:flex;justify-content:space-between;font-size:11px;padding:2px 0"><span style="color:${esc(a.color || 'var(--t3)')}">${esc(a.label)}</span><span>${fT(a.amount)}</span></div>`).join('')
      : (q.status === 'paid' ? `<button class="btn btn-ghost" style="font-size:11px;width:100%;margin-top:4px" data-action="allocateBonus" data-args='["${esc(q.id)}"]'>Allocate Funds</button>` : '');

    return `<div class="card" style="padding:16px;border-left:3px solid ${q.status === 'paid' || q.status === 'allocated' ? 'var(--grn)' : q.status === 'earned' ? 'var(--yel)' : 'var(--c3)'}">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px">
        <span style="font-weight:600;font-size:15px">${esc(q.quarter)}</span>
        ${_statusBadge(q.status)}
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;font-size:13px">
        <div><span style="color:var(--t3)">Gross EUR</span><br><strong>${fE(q.grossEUR)}</strong></div>
        <div><span style="color:var(--t3)">FX Rate</span><br><strong>${q.fxRate ? f(q.fxRate, 2) : '~' + f(fxRate, 2)}</strong></div>
        <div><span style="color:var(--t3)">Gross TRY</span><br><strong>${fT(estGrossTRY)}</strong></div>
        <div><span style="color:var(--t3)">Net TRY</span><br><strong style="color:${hasNet ? 'var(--grn)' : 'var(--t2)'}">${hasNet ? fT(q.netTRY) : '~' + fT(estNetTRY)}</strong></div>
      </div>
      <div style="margin-top:6px;font-size:12px;color:var(--t3)">Net/Gross: <strong>${fP(estNetBrut)}</strong></div>
      ${q.note ? '<div style="margin-top:4px;font-size:11px;color:var(--t3);font-style:italic">' + esc(q.note) + '</div>' : ''}
      ${allocHTML ? '<div style="margin-top:8px;border-top:1px solid var(--c3);padding-top:6px">' + allocHTML + '</div>' : ''}
      <div style="display:flex;gap:4px;margin-top:8px">
        <button class="btn btn-ghost" style="font-size:11px;flex:1" data-action="openBonusQuarter" data-args='["${esc(q.id)}"]'>Edit</button>
        ${q.status === 'earned' ? `<button class="btn btn-ghost" style="font-size:11px;flex:1;color:var(--grn)" data-action="markBonusPaid" data-args='["${esc(q.id)}"]'>Mark Paid</button>` : ''}
      </div>
    </div>`;
  }).join('');

  // ─── Allocation Framework (fw computed above for the hero) ───
  const fwHTML = fw.length > 0 ? `
    <div class="mny-lbl">ALLOCATION FRAMEWORK · LOCKED SPLIT</div>
    <div class="card" style="padding:16px;border-radius:14px">
      <div style="display:flex;gap:4px;height:24px;border-radius:12px;overflow:hidden;margin-bottom:10px">
        ${fw.map(b => `<div style="flex:${b.pct};background:${esc(b.color)};display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:600;color:#000">${b.pct}%</div>`).join('')}
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:8px">
        ${fw.map(b => `<div style="font-size:12px"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${esc(b.color)};margin-right:4px"></span>${esc(b.label)} <strong>${b.pct}%</strong></div>`).join('')}
      </div>
    </div>` : '';

  // ─── Annual Summary — mny expandable row ───
  const summaryHTML = `
    <div class="mny-row" id="mnyrow-bnsum" style="--ac:var(--gold);margin-top:14px">
      <div class="mny-rmain" data-action="mnyToggle" data-args='["mnyrow-bnsum"]'>
        <span class="dot"></span><span class="rt">Annual Summary</span>
        <span class="rsum"><span class="pos">${fT(totalNetTRY)} net</span> · ${quarters.length} quarter${quarters.length === 1 ? '' : 's'}</span>
        <span class="chev">▾</span>
      </div>
      <div class="mny-rdetail"><div class="mny-rinner">
      <div class="table-wrap"><table class="tbl">
        <thead><tr><th>Quarter</th><th>Gross EUR</th><th>FX</th><th>Gross TRY</th><th>Net TRY</th><th>Net/Gross</th><th>Status</th></tr></thead>
        <tbody>
          ${quarters.map(q => `<tr>
            <td>${esc(q.quarter)}</td>
            <td>${fE(q.grossEUR)}</td>
            <td>${q.fxRate ? f(q.fxRate, 2) : '—'}</td>
            <td>${q.grossTRY ? fT(q.grossTRY) : '—'}</td>
            <td style="color:${q.netTRY ? 'var(--grn)' : 'var(--t3)'}">${q.netTRY ? fT(q.netTRY) : '—'}</td>
            <td>${q.netBrutRatio ? fP(q.netBrutRatio) : '—'}</td>
            <td>${_statusBadge(q.status)}</td>
          </tr>`).join('')}
          <tr style="font-weight:600;border-top:2px solid var(--c3)">
            <td>Total</td>
            <td>${fE(earnedEUR)}</td>
            <td>—</td>
            <td>${fT(totalGrossTRY)}</td>
            <td style="color:var(--grn)">${fT(totalNetTRY)}</td>
            <td>${avgNetBrut > 0 ? fP(avgNetBrut) : '—'}</td>
            <td></td>
          </tr>
        </tbody>
      </table></div>
      </div></div>
    </div>`;

  el.innerHTML = heroHTML +
    _bonusScanSection(scanQ) +
    `<div class="mny-lbl">QUARTERS · ${esc(plan.fiscalYear)}</div>` +
    '<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:12px;margin-bottom:16px">' + qCards + '</div>' +
    fwHTML + summaryHTML +
    '<div class="mny-foot">PRE-ALLOCATION IS THE DISCIPLINE · EXPLICIT CURRENCY EVERYWHERE</div>';
}

// ─── Quarter CRUD ───
export function openBonusQuarter(qId, planId, quarterLabel) {
  // Remove stale modal if re-opened
  const stale = document.getElementById('moBonusQ');
  if (stale) stale.remove();
  const existing = qId ? (S.bonusQuarters || []).find(q => q.id === qId) : null;
  const isEdit = !!existing;

  const q = existing || {
    quarter: quarterLabel || 'Q1',
    planId: planId,
    status: 'projected',
    grossEUR: 0,
    fxRate: null,
    grossTRY: null,
    netTRY: null,
    netBrutRatio: null,
    earnedDate: null,
    paidDate: null,
    note: ''
  };

  const html = `<div class="mo active" id="moBonusQ" role="dialog" aria-modal="true">
    <div class="mod" style="max-width:480px">
      <div class="mod-t" style="display:flex;justify-content:space-between;align-items:center">${isEdit ? 'Edit' : 'Add'} Bonus Quarter <span style="cursor:pointer;font-size:20px;color:var(--t3)" data-action="clMo" data-args='["moBonusQ"]'>&times;</span></div>
      <div style="display:grid;gap:12px">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
          <label>Quarter<select id="bqQuarter"><option ${q.quarter === 'Q1' ? 'selected' : ''}>Q1</option><option ${q.quarter === 'Q2' ? 'selected' : ''}>Q2</option><option ${q.quarter === 'Q3' ? 'selected' : ''}>Q3</option><option ${q.quarter === 'Q4' ? 'selected' : ''}>Q4</option></select></label>
          <label>Status<select id="bqStatus"><option value="projected" ${q.status === 'projected' ? 'selected' : ''}>Projected</option><option value="earned" ${q.status === 'earned' ? 'selected' : ''}>Earned</option><option value="paid" ${q.status === 'paid' ? 'selected' : ''}>Paid</option><option value="allocated" ${q.status === 'allocated' ? 'selected' : ''}>Allocated</option></select></label>
        </div>
        <label>Gross EUR<input type="number" id="bqGrossEUR" value="${q.grossEUR || ''}" step="0.01"></label>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
          <label>FX Rate (EUR/TRY)<input type="number" id="bqFxRate" value="${q.fxRate || ''}" step="0.01" placeholder="Auto from live rate"></label>
          <label>Gross TRY<input type="number" id="bqGrossTRY" value="${q.grossTRY || ''}" step="0.01" placeholder="Auto-calc"></label>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
          <label>Net TRY<input type="number" id="bqNetTRY" value="${q.netTRY || ''}" step="0.01" placeholder="After tax"></label>
          <label>Net/Gross Ratio<input type="number" id="bqNetBrut" value="${q.netBrutRatio ? (q.netBrutRatio * 100).toFixed(1) : ''}" step="0.1" placeholder="e.g. 64.2"></label>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
          <label>Earned Date<input type="date" id="bqEarnedDate" value="${q.earnedDate || ''}"></label>
          <label>Paid Date<input type="date" id="bqPaidDate" value="${q.paidDate || ''}"></label>
        </div>
        <label>Note<input type="text" id="bqNote" value="${esc(q.note || '')}" placeholder="Optional note"></label>
      </div>
      <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:16px">
        ${isEdit ? `<button class="btn btn-d" data-action="deleteBonusQuarter" data-args='["${esc(q.id)}"]'>Delete</button>` : ''}
        <button class="btn btn-p" data-action="saveBonusQuarter" data-args='["${isEdit ? esc(q.id) : ''}","${esc(q.planId || planId)}"]'>Save</button>
      </div>
    </div>
  </div>`;

  document.body.insertAdjacentHTML('beforeend', html);

  // Auto-calc gross TRY when EUR or FX changes
  const eurInput = document.getElementById('bqGrossEUR');
  const fxInput = document.getElementById('bqFxRate');
  const grossTRYInput = document.getElementById('bqGrossTRY');
  const autoCalc = () => {
    const eur = parseFloat(eurInput.value) || 0;
    const fx = (parseFloat(fxInput.value) > 0 ? parseFloat(fxInput.value) : null) || fxEURTRY();
    if (eur > 0 && !grossTRYInput.value) {
      // The hint is a preview of what will be SAVED, so it must not preview a number the save
      // would refuse to produce.
      grossTRYInput.placeholder = fx ? fT(eur * fx) : 'enter the FX rate';
    }
  };
  eurInput.addEventListener('input', autoCalc);
  fxInput.addEventListener('input', autoCalc);
}

export function saveBonusQuarter(qId, planId) {
  const grossEUR = parseFloat(document.getElementById('bqGrossEUR').value) || 0;
  const fxRateVal = parseFloat(document.getElementById('bqFxRate').value) || null;
  // This one WRITES. A fabricated 53 here does not merely render wrong, it is persisted as the
  // quarter's gross and every later reader treats it as a figure the owner supplied.
  const _fxForSave = fxRateVal || fxEURTRY();
  const _typedTRY = parseFloat(document.getElementById('bqGrossTRY').value) || 0;
  if (!_typedTRY && grossEUR > 0 && !_fxForSave) {
    return toast('No EUR/TRY rate — enter one, or type the gross TRY directly. It will not be estimated.', 'a-r', 7000);
  }
  const grossTRY = _typedTRY || (grossEUR * _fxForSave);
  const netTRY = parseFloat(document.getElementById('bqNetTRY').value) || null;
  const netBrutPct = parseFloat(document.getElementById('bqNetBrut').value);
  const netBrutRatio = netBrutPct > 0 ? netBrutPct / 100 : (netTRY && grossTRY ? netTRY / grossTRY : null);

  const data = {
    quarter: document.getElementById('bqQuarter').value,
    status: document.getElementById('bqStatus').value,
    grossEUR,
    fxRate: fxRateVal,
    grossTRY: grossTRY || null,
    netTRY,
    netBrutRatio,
    earnedDate: document.getElementById('bqEarnedDate').value || null,
    paidDate: document.getElementById('bqPaidDate').value || null,
    note: document.getElementById('bqNote').value.trim()
  };

  if (!S.bonusQuarters) S.bonusQuarters = [];

  if (qId) {
    const idx = S.bonusQuarters.findIndex(q => q.id === qId);
    if (idx >= 0) {
      Object.assign(S.bonusQuarters[idx], data);
      stampItem(S.bonusQuarters[idx]);
    }
  } else {
    const newQ = { id: uid(), planId, ...data, _createdAt: Date.now() };
    stampItem(newQ);
    S.bonusQuarters.push(newQ);
  }

  save();
  clMo('moBonusQ');
  renderBonus();
  toast('Quarter saved', 'a-b');
}

export async function deleteBonusQuarter(qId) {
  if (!await uiConfirm('Delete this quarter record?', { title: 'Delete quarter', okText: 'Delete', danger: true })) return;
  S.bonusQuarters = (S.bonusQuarters || []).filter(q => q.id !== qId);
  tombstoneId(qId);
  save();
  clMo('moBonusQ');
  renderBonus();
  toast('Quarter deleted', 'a-b');
}

export function markBonusPaid(qId) {
  const q = (S.bonusQuarters || []).find(x => x.id === qId);
  if (!q) return;
  q.status = 'paid';
  q.paidDate = q.paidDate || now().slice(0, 10);
  stampItem(q);
  save();
  renderBonus();
  toast(q.quarter + ' marked as paid', 'a-b');
}

export function switchBonusPlan(planId) {
  _selectedPlanId = planId;
  renderBonus();
}

// ─── Plan CRUD ───
export function openBonusPlan(planId) {
  const stale = document.getElementById('moBonusPlan');
  if (stale) stale.remove();
  const existing = planId ? (S.bonusPlans || []).find(p => p.id === planId) : null;
  const isEdit = !!existing;
  const p = existing || { label: '', fiscalYear: '', startDate: '', endDate: '', targetEUR: 0, currency: 'EUR', allocFramework: [] };

  const html = `<div class="mo active" id="moBonusPlan" role="dialog" aria-modal="true">
    <div class="mod" style="max-width:420px">
      <div class="mod-t" style="display:flex;justify-content:space-between;align-items:center">${isEdit ? 'Edit' : 'New'} Bonus Plan <span style="cursor:pointer;font-size:20px;color:var(--t3)" data-action="clMo" data-args='["moBonusPlan"]'>&times;</span></div>
      <div style="display:grid;gap:12px">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
          <label>Label<input type="text" id="bpLabel" value="${esc(p.label)}" placeholder="e.g. FY26"></label>
          <label>Fiscal Year<input type="text" id="bpFY" value="${esc(p.fiscalYear)}" placeholder="e.g. FY26"></label>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
          <label>Start Date<input type="date" id="bpStart" value="${p.startDate || ''}"></label>
          <label>End Date<input type="date" id="bpEnd" value="${p.endDate || ''}"></label>
        </div>
        <label>Annual Target (EUR)<input type="number" id="bpTarget" value="${p.targetEUR || ''}" step="0.01"></label>
      </div>
      <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:16px">
        ${isEdit ? `<button class="btn btn-d" data-action="deleteBonusPlan" data-args='["${esc(p.id)}"]'>Delete</button>` : ''}
        <button class="btn btn-p" data-action="saveBonusPlan" data-args='["${isEdit ? esc(p.id) : ''}"]'>Save</button>
      </div>
    </div>
  </div>`;

  document.body.insertAdjacentHTML('beforeend', html);
}

export function saveBonusPlan(planId) {
  const data = {
    label: document.getElementById('bpLabel').value.trim(),
    fiscalYear: document.getElementById('bpFY').value.trim(),
    startDate: document.getElementById('bpStart').value || null,
    endDate: document.getElementById('bpEnd').value || null,
    targetEUR: parseFloat(document.getElementById('bpTarget').value) || 0,
    currency: 'EUR'
  };

  if (!data.label) { toast('Label required', 'a-b'); return; }
  if (!S.bonusPlans) S.bonusPlans = [];

  if (planId) {
    const idx = S.bonusPlans.findIndex(p => p.id === planId);
    if (idx >= 0) {
      Object.assign(S.bonusPlans[idx], data);
      stampItem(S.bonusPlans[idx]);
    }
  } else {
    const newP = { id: uid(), ...data, allocFramework: [
      { label: 'Liquid (deposits/daily)', pct: 22.5, color: '#4ecdc4' },
      { label: 'Portfolio (IBKR + Garanti)', pct: 42.5, color: '#3b82f6' },
      { label: 'Tactical (BTC/BIST)', pct: 22.5, color: 'var(--yel)' },
      { label: 'Personal', pct: 12.5, color: '#8b5cf6' }
    ], _createdAt: Date.now() };
    stampItem(newP);
    S.bonusPlans.push(newP);
    _selectedPlanId = newP.id;
  }

  save();
  clMo('moBonusPlan');
  renderBonus();
  toast('Plan saved', 'a-b');
}

export async function deleteBonusPlan(planId) {
  if (!await uiConfirm('Delete this plan and all its quarters?', { title: 'Delete bonus plan', okText: 'Delete', danger: true })) return;
  // Track deleted IDs for cloud sync. tombstoneId is the one primitive: it also stamps
  // `_deletionTimestamps`, which the 1,000-tombstone retention cap sorts by — a bare push
  // leaves the id undated, and the cap then backfills it as if it were the newest deletion.
  tombstoneId(planId);
  const orphanQs = (S.bonusQuarters || []).filter(q => q.planId === planId);
  orphanQs.forEach(q => tombstoneId(q.id));
  S.bonusPlans = (S.bonusPlans || []).filter(p => p.id !== planId);
  S.bonusQuarters = (S.bonusQuarters || []).filter(q => q.planId !== planId);
  _selectedPlanId = null;
  save();
  renderBonus();
  toast('Plan deleted', 'a-b');
}

// ─── Allocation ───
export function allocateBonus(qId) {
  const stale = document.getElementById('moAllocBonus');
  if (stale) stale.remove();
  const q = (S.bonusQuarters || []).find(x => x.id === qId);
  if (!q || !q.netTRY) { toast('No net amount to allocate', 'a-b'); return; }
  const plan = (S.bonusPlans || []).find(p => p.id === q.planId);
  const fw = plan?.allocFramework || [];
  if (!fw.length) { toast('No allocation framework defined', 'a-b'); return; }

  const net = q.netTRY;
  const allocs = fw.map(bucket => ({
    label: bucket.label,
    pct: bucket.pct,
    amount: Math.round(net * bucket.pct / 100),
    color: bucket.color,
    executed: false,
    outcome: null
  }));

  // Show modal with pre-calculated allocation
  const rows = allocs.map((a, i) => `
    <div style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--c3)">
      <span style="width:8px;height:8px;border-radius:50%;background:${esc(a.color)}"></span>
      <span style="flex:1;font-size:13px">${esc(a.label)}</span>
      <input type="number" id="allocAmt_${i}" value="${a.amount}" style="width:100px;text-align:right" step="1">
      <span style="font-size:11px;color:var(--t3)">${a.pct}%</span>
    </div>
  `).join('');

  const html = `<div class="mo active" id="moAllocBonus" role="dialog" aria-modal="true">
    <div class="mod" style="max-width:420px">
      <div class="mod-t" style="display:flex;justify-content:space-between;align-items:center">Allocate ${esc(q.quarter)} — ${fT(net)} <span style="cursor:pointer;font-size:20px;color:var(--t3)" data-action="clMo" data-args='["moAllocBonus"]'>&times;</span></div>
      <div>${rows}</div>
      <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:16px">
        <button class="btn btn-p" data-action="saveAllocation" data-args='["${esc(qId)}", ${allocs.length}]'>Confirm Allocation</button>
      </div>
    </div>
  </div>`;

  document.body.insertAdjacentHTML('beforeend', html);
}

export function saveAllocation(qId, count) {
  const q = (S.bonusQuarters || []).find(x => x.id === qId);
  if (!q) return;
  const plan = (S.bonusPlans || []).find(p => p.id === q.planId);
  const fw = plan?.allocFramework || [];

  const allocs = [];
  for (let i = 0; i < count; i++) {
    const input = document.getElementById('allocAmt_' + i);
    const amt = parseFloat(input?.value) || 0;
    allocs.push({
      label: fw[i]?.label || 'Slot ' + (i + 1),
      pct: fw[i]?.pct || 0,
      amount: amt,
      color: fw[i]?.color || 'var(--t3)',
      executed: false,
      outcome: null
    });
  }

  q.allocations = allocs;
  q.status = 'allocated';
  stampItem(q);
  save();
  clMo('moAllocBonus');
  renderBonus();
  toast(q.quarter + ' allocation saved', 'a-b');
}
